/**
 * The campaign's collection agenda (platform design §6.4; Figma `273:8581`). A window around now,
 * not a history: forward it is the plan the ledger holds, backward it reaches as far as the
 * ledger's own retention, and the footer names that bound (`AgendaOut.earliest`) instead of
 * rendering slots the reaper has already emptied.
 *
 * The heading stays put and the cards scroll inside their own region, so the screen's identity
 * never leaves the viewport in a list with no last page.
 */

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { HintedIcon } from '../../../chrome/hint';
import { PageHeader } from '../../../chrome/page';
import { useViewSearch } from '../../../chrome/viewSearch';
import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale, type Locale } from '../../../i18n';
import { compact, integer } from '../../../i18n/format';
import { Chip } from '../../../ui/chip';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import { ScrollArea } from '../../../ui/scroll-area';
import { PlatformIcon, platformLabel } from '../accounts/NoSelection';
import { acquisitionState, useCredits } from '../../tenant/balance';
import { groupSlots, slotSummary, type SlotGroup, type SlotSummary } from './agendaGrouping';
import { useAgenda } from './api/queries';
import { DayPicker, dayWindow } from './DayPicker';
import { dayHeading, dayKey, formatDate, formatTime, startOfDay } from './dayHeading';
import { cm, cmPlural } from './messages';
import { SlotChip } from './SlotChip';
import { WINDOW_OPTIONS, windowFor, WindowSelect, type WindowOption } from './WindowSelect';

function summaryLine(summary: SlotSummary, blocked: boolean, locale: Locale): string {
  const total = integer(summary.total, locale);
  if (summary.done === 0 && summary.failed === 0) {
    return cmPlural(blocked ? 'slot.noCredit.other' : 'slot.scheduled.other', summary.total, {
      count: total,
    });
  }
  const parts = [
    cmPlural('slot.done.other', summary.total, {
      done: integer(summary.done, locale),
      count: total,
    }),
  ];
  if (summary.failed > 0) {
    parts.push(
      cmPlural('slot.failed.other', summary.failed, { count: integer(summary.failed, locale) }),
    );
  }
  return parts.join(' · ');
}

function Legend(props: { blocked: boolean }): ReactNode {
  const entries: { key: string; dot: string; label: string }[] = [
    { key: 'done', dot: 'bg-primary', label: cm('agenda.legend.done') },
    ...(props.blocked
      ? [{ key: 'noCredit', dot: 'bg-muted-foreground', label: cm('agenda.legend.noCredit') }]
      : [
          { key: 'planned', dot: 'bg-muted-foreground', label: cm('agenda.legend.planned') },
          { key: 'dispatched', dot: 'bg-muted-foreground', label: cm('agenda.legend.dispatched') },
        ]),
    { key: 'failed', dot: 'bg-destructive', label: cm('agenda.legend.failed') },
  ];
  return (
    <ul
      aria-label={cm('agenda.legend.label')}
      className="text-caption text-muted-foreground flex flex-wrap gap-x-4 gap-y-1"
    >
      {entries.map((entry) => (
        <li key={entry.key} className="flex items-center gap-1.5">
          <span aria-hidden className={`size-2 rounded-full ${entry.dot}`} />
          {entry.label}
        </li>
      ))}
    </ul>
  );
}

function SlotCard(props: { group: SlotGroup; blocked: boolean; locale: Locale }): ReactNode {
  const { group, blocked, locale } = props;
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 px-4">
        <h3 className="text-body font-medium">
          <time dateTime={group.scheduledSlot}>
            {formatTime(new Date(group.scheduledSlot), locale)}
          </time>
        </h3>
        <p className="text-caption text-muted-foreground">
          {summaryLine(slotSummary(group), blocked, locale)}
        </p>
      </CardHeader>
      <CardContent className="px-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {group.accounts.map((account) => (
            <div
              key={account.key}
              className="flex flex-col gap-2 rounded-lg border p-md"
              data-testid="agenda-account-card"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  {account.platform !== null && (
                    <HintedIcon label={platformLabel(account.platform)}>
                      <PlatformIcon platform={account.platform} className="size-3 shrink-0" />
                    </HintedIcon>
                  )}
                  <p className="truncate text-body-sm font-medium">{account.label}</p>
                </span>
                {account.followerCount !== null && (
                  <span className="shrink-0 text-caption text-muted-foreground">
                    {compact(account.followerCount, locale)}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {account.jobs.map((job, index) => (
                  <SlotChip
                    key={`${job.jobType}-${index}`}
                    jobType={job.jobType}
                    state={job.state}
                    blocked={blocked}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function Agenda(props: {
  campaignId: string;
  tenantId: string;
  /** Pins "now" so a story or a test renders the same day headings on every run. */
  now?: Date;
}): ReactNode {
  const locale = getLocale();
  const now = useMemo(() => props.now ?? new Date(), [props.now]);

  const [values, set] = useViewSearch(`agenda.${props.campaignId}`, {
    window: { default: 'today', values: WINDOW_OPTIONS },
  });
  const windowOption = values.window as WindowOption;
  const setWindowOption = (next: WindowOption): void => set({ window: next });
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const range = useMemo(
    () => (selectedDay !== null ? dayWindow(selectedDay) : windowFor(windowOption, now)),
    [selectedDay, windowOption, now],
  );

  const credits = useCredits(props.tenantId);
  const agenda = useAgenda(props.campaignId, range);
  const blocked =
    credits.data !== undefined && acquisitionState(credits.data) === 'waiting-on-credits';
  // `latest` is the window's exclusive upper bound; the last day it actually covers is one
  // millisecond inside it.
  const earliestBound = agenda.data?.earliest != null ? new Date(agenda.data.earliest) : null;
  const latestBound =
    agenda.data?.latest != null ? new Date(new Date(agenda.data.latest).getTime() - 1) : null;

  return (
    // 6.5rem is the shell's sticky header (h-14) plus the content area's vertical padding
    // (py-6 twice): the agenda fills what is left of the viewport, so only the cards scroll.
    <div className="flex min-h-96 flex-col gap-4" style={{ height: 'calc(100svh - 6.5rem)' }}>
      <div className="flex shrink-0 flex-col gap-3">
        <PageHeader
          title={cm('agenda.title')}
          actions={
            <>
              {blocked && <Chip tone="negative">{cm('agenda.noCredit')}</Chip>}
              <WindowSelect
                value={windowOption}
                onChange={(option) => {
                  setWindowOption(option);
                  setSelectedDay(null);
                }}
              />
              <DayPicker
                value={selectedDay ?? startOfDay(now)}
                onChange={setSelectedDay}
                min={earliestBound}
                max={latestBound}
                disabled={agenda.data === undefined || agenda.data.earliest === null}
              />
            </>
          }
        />
        {credits.data !== undefined && <Legend blocked={blocked} />}
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="pr-3 pb-4">
          {/* Whether a slot reads "agendada" or "sem crédito" depends on the balance, so no slot
              renders until the balance has answered. */}
          <QueryStates query={credits} empty={null}>
            {() => (
              // Emptiness has two meanings here, told apart inside the render: nothing recorded
              // at all, and nothing inside this window.
              <QueryStates query={agenda} isEmpty={() => false} empty={null}>
                {(data) => {
                  if (data.earliest === null) {
                    return (
                      <EmptyState
                        title={cm('agenda.absentWindow.title')}
                        description={cm('agenda.absentWindow.description')}
                        tone="neutral"
                      />
                    );
                  }
                  const groups = groupSlots(data.slots).reverse();
                  const earliest = new Date(data.earliest);
                  return (
                    <div className="flex flex-col gap-4">
                      {groups.length === 0 ? (
                        <EmptyState title={cm('agenda.empty.title')} tone="neutral" />
                      ) : (
                        <ol className="flex flex-col gap-4">
                          {groups.map((group, index) => {
                            const date = new Date(group.scheduledSlot);
                            const previous = groups[index - 1];
                            const newDay =
                              previous === undefined ||
                              dayKey(new Date(previous.scheduledSlot)) !== dayKey(date);
                            return (
                              <li key={group.scheduledSlot} className="flex flex-col gap-3">
                                {newDay && (
                                  <h2 className="text-caption font-medium text-muted-foreground">
                                    {dayHeading(date, now)}
                                  </h2>
                                )}
                                <SlotCard group={group} blocked={blocked} locale={locale} />
                              </li>
                            );
                          })}
                        </ol>
                      )}
                      <p className="text-caption text-muted-foreground text-center">
                        {cm('agenda.window', {
                          when: `${formatDate(earliest, locale)} ${formatTime(earliest, locale)}`,
                        })}
                      </p>
                    </div>
                  );
                }}
              </QueryStates>
            )}
          </QueryStates>
        </div>
      </ScrollArea>
    </div>
  );
}
